/**
 * 표 페이지 공용 클라이언트 측 엑셀 export.
 *
 * 목적: 사용자가 컬럼 토글로 숨긴 컬럼이 엑셀 다운로드에도 반영되도록.
 * 화면에 보이는 컬럼 순서·라벨 그대로 시트 생성.
 *
 * 사용:
 *   exportRowsToExcel({
 *     filename: '영업진행_20260516.xlsx',
 *     sheetName: '영업진행',
 *     columns: visibleColumns,  // DataTable Column<T>[]
 *     rows: visibleRows,        // 정렬·필터 적용된 표시 행
 *     totals: { proposalPrice: 1234567 },  // (옵션) 합계 행
 *   });
 */

import * as XLSX from 'xlsx';
import type { Column } from '../components/DataTable';

export interface ExportOptions<T> {
  filename: string;
  sheetName: string;
  columns: Column<T>[];
  rows: T[];
  /** key → 합계값. 지정 컬럼만 마지막 합계 행에 표시. */
  totals?: Record<string, number>;
  /** key → 컬럼별 너비(엑셀 width). 미지정 시 type 기반 자동값. */
  colWidths?: Record<string, number>;
}

function autoWidth(type?: string): number {
  if (type === 'number') return 14;
  if (type === 'date') return 12;
  return 18;
}

function cellFor<T>(col: Column<T>, row: T): string | number {
  const raw = col.accessor(row);
  if (raw === null || raw === undefined) return '';
  if (col.type === 'number') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : '';
  }
  return String(raw);
}

export function exportRowsToExcel<T>(opts: ExportOptions<T>): void {
  const { filename, sheetName, columns, rows, totals, colWidths } = opts;

  // 헤더 — 컬럼 label.
  const header: string[] = ['No', ...columns.map((c) => c.label)];

  const data: (string | number)[][] = [header];
  rows.forEach((row, i) => {
    data.push([i + 1, ...columns.map((c) => cellFor(c, row))]);
  });

  // 합계 행 (옵션)
  if (totals) {
    const totalRow: (string | number)[] = ['', '합계'];
    let placedTotal = false;
    columns.slice(1).forEach((c) => {
      if (totals[c.key] !== undefined) {
        totalRow.push(totals[c.key]!);
        placedTotal = true;
      } else {
        totalRow.push('');
      }
    });
    if (placedTotal) data.push(totalRow);
  }

  const sheet = XLSX.utils.aoa_to_sheet(data);
  sheet['!cols'] = [
    { wch: 5 },
    ...columns.map((c) => ({
      wch: colWidths?.[c.key] ?? autoWidth(c.type),
    })),
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, sheetName);

  const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** 오늘 날짜 yyyymmdd 형태 (파일명용). */
export function dateStamp(): string {
  const d = new Date();
  return (
    String(d.getFullYear()) +
    String(d.getMonth() + 1).padStart(2, '0') +
    String(d.getDate()).padStart(2, '0')
  );
}
